import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    effect,
    inject,
    signal
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSidenav } from "@angular/material/sidenav";
import { SelectOption } from "@app/shared/components/dsn/custom-select/custom-select.component";
import { NandaChatComponent } from "@app/shared/components/nanda-chat/nanda-chat.component";
import { NandaIconComponent } from "@app/shared/components/nanda-icon/nanda-icon.component";
import { ReportFeedbackComponent } from "@app/shared/components/report-feedback/report-feedback.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { AccountingPlanService } from "@app/shared/services/accounting-plan.service";
import { ChatNandaService } from "@app/shared/services/chat-nanda.service";
import { ContactService } from "@app/shared/services/contact.service";
import { DeviceService } from "@app/shared/services/device.service";
import { DrawerService } from "@app/shared/services/drawer.service";
import { ReportNotesNavigationService } from "@app/shared/services/report-notes-navigation.service";
import { VisualizationSortService } from "@app/shared/services/visualization-sort.service";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ReportPreferenceService } from "@arch/domain/application/services/report-preference.service";
import { CompanyEntity, ReportEntity } from "@arch/domain/entity";
import { ScenarioEntity } from "@arch/domain/entity/scenario.entity";
import { PopoverComponent, PopoverTemplate } from "@ngx-popovers/popover";
import { OrderVisualizationsOptionsComponent } from "../../components/order-visualizations-options/order-visualizations-options.component";
import { ReportBeachmarkingSectionComponent } from "../../components/report-beachmarking-section/report-beachmarking-section.component";
import { ReportDashboardSidenavListComponent } from "../../components/report-dashboard-sidenav-list/report-dashboard-sidenav-list.component";
import { ReportHeaderComponent } from "../../components/report-header/report-header.component";
import { ReportPerformanceComponent } from "../../components/report-performance/report-performance.component";
import {
    EventChangeComparationParams,
    EventChangePeriodParams,
    ReportToolbarComponent
} from "../../components/report-toolbar/report-toolbar.component";
import { ReportVisualizationComponent } from "../../components/report-visualization/report-visualization.component";
export interface ExtendedSelectOption extends SelectOption {
    color?: string;
    subtitle?: string;
}

@Component({
    selector: "app-desktop-dashboard",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        SharedMaterialModule,
        ReportHeaderComponent,
        ReportToolbarComponent,
        ReportBeachmarkingSectionComponent,
        ReportPerformanceComponent,
        ReportDashboardSidenavListComponent,
        ReportVisualizationComponent,
        ReportFeedbackComponent,
        NandaChatComponent,
        NandaIconComponent,
        PopoverComponent,
        PopoverTemplate,
        OrderVisualizationsOptionsComponent
    ],
    templateUrl: "./desktop.component.html",
    styleUrl: "./desktop.component.scss"
})
export class DesktopComponent implements AfterViewInit {
    @ViewChild("drawer", { static: false }) drawer: MatSidenav;
    @ViewChild(PopoverComponent) popoverOrder: PopoverComponent;

    @Input({ required: true }) loading: boolean;
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: false }) set accordionState(value: string) {
        this.accordionStateSignal.set(value);
    }
    @Input() previewMode?: boolean = false;
    @Input() isPreviewMode?: boolean = false;
    private readonly accordionStateSignal = signal<string>(this.accordionState);
    @Output() handleChangePeriod: EventEmitter<EventChangePeriodParams> =
        new EventEmitter();
    @Output()
    handleChangeComparation: EventEmitter<EventChangeComparationParams> =
        new EventEmitter();
    @Output() handleResetPeriod: EventEmitter<void> = new EventEmitter();
    @Output() handleChangeBudget: EventEmitter<ScenarioEntity> =
        new EventEmitter();
    @Output() handleResetBudget: EventEmitter<void> = new EventEmitter();
    @Output() handleChangeProjection: EventEmitter<ScenarioEntity> =
        new EventEmitter();
    @Output() handleResetProjection: EventEmitter<void> = new EventEmitter();
    @Output() handleChangeGoals: EventEmitter<void> = new EventEmitter();
    @Output() handleResetGoals: EventEmitter<void> = new EventEmitter();

    public readonly preferenceService = inject(ReportPreferenceService);
    public readonly chatNandaService = inject(ChatNandaService);
    private readonly drawerService = inject(DrawerService);
    private readonly deviceService = inject(DeviceService);
    private readonly reportNotesNavigationService = inject(
        ReportNotesNavigationService
    );
    private readonly visualizationSortService = inject(
        VisualizationSortService
    );
    private readonly eventsService = inject(EventsService);
    private readonly contactService = inject(ContactService);
    public readonly accountingPlanService = inject(AccountingPlanService);

    public isMobile = this.deviceService.getIsMobile();
    public contact = this.contactService.get();
    public sectionStates: { [key: string]: boolean } = {
        SectionBenchmarking: true,
        SectionPerformance: true,
        SectionNavigationTips: true,
        SectionIndicators: true,
        SectionFeedback: true
    };

    // Opções de ordenação definidas diretamente no HTML
    public selectedOrder: string = "Relevância";
    public orderOptions = this.visualizationSortService.sortOptions;

    public hasBenchmarking: boolean = true;

    constructor(private dialog: MatDialog) {
        effect(() => {
            const accordionState = this.accordionStateSignal();
            this.onListenerAccordion(accordionState);
        });
    }

    ngAfterViewInit() {
        this.drawerService.drawerState$.subscribe((state) => {
            if (this.drawer) {
                if (state) {
                    this.drawer.open();
                } else {
                    this.drawer.close();
                }
            }
        });

        if (this.drawer) {
            this.drawer.openedChange.subscribe((opened) => {
                if (!opened) {
                    this.drawerService.closeDrawer();
                }
            });
        }
    }

    public toggleSection(
        event: boolean,
        index?: number,
        sectionId?: string
    ): void {}

    public toggleDrawer(): void {
        if (this.drawer) {
            this.drawerService.toggleDrawer();
        }
    }

    private onListenerAccordion(accodionId: any): void {
        if (!accodionId) return;
        Object.keys(this.sectionStates).forEach((key) => {
            this.sectionStates[key] = false;
        });
        this.sectionStates[accodionId] = true;
    }

    public onChangePeriod(event: EventChangePeriodParams): void {
        this.handleChangePeriod.emit(event);
    }

    public onChangeComparation(event: EventChangeComparationParams): void {
        this.handleChangeComparation.emit(event);
    }

    public handleNavigation(event: {
        id: string;
        sectionId?: string;
        visualizationId?: string;
    }): void {
        this.drawerService.closeDrawer();
        Object.keys(this.sectionStates).forEach((key) => {
            this.sectionStates[key] = false;
        });
        this.sectionStates[event.id] = true;
        if (event.visualizationId) {
            setTimeout(() => {
                const element = document.getElementById(event.visualizationId);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                    });
                }
            }, 100);
        }
    }

    public onOrderChange(option: SelectOption): void {
        this.selectedOrder = option.label;
    }

    public get sortedVisualizations() {
        const visualizationsVisible = this.report.getVisualizationsVisible();
        if (!visualizationsVisible?.length) {
            return [];
        }
        return this.visualizationSortService.sortVisualizations(
            visualizationsVisible,
            this.selectedOrder
        );
    }

    public onBenchmarkingDataLoaded(hasBenchmarkingData: boolean): void {
        this.hasBenchmarking = hasBenchmarkingData;
        this.reportNotesNavigationService.setHasBenchmarkingData(
            hasBenchmarkingData
        );
    }

    public onOpenChat(): void {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.abrir-nanda`
        );
    }
}
